// 매장 API — /api/v2/tenants/*
//
// - /me/* : 매장 자체 설정 (서식 기본값 등). 마진은 AuthContext 에서 직접 호출.
// - /        : 사용자가 속한 매장 목록 + 현재 매장.
// - /switch  : 현재 매장 전환.

import { authJson } from "./_client";
import {
  normalizeSignaturePenWidth,
} from "../../shared/signature-stroke";

export {
  DEFAULT_SIGNATURE_PEN_WIDTH,
  SIGNATURE_PEN_WIDTH_MAX,
  SIGNATURE_PEN_WIDTH_MIN,
} from "../../shared/signature-stroke";

const API_V2 = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2`;

const fetchJson = <T>(url: string, init?: RequestInit): Promise<T> =>
  authJson<T>(url, init);

export type FormDefaults = Record<string, string>;

/** 매장이 순서 편집에서 직접 만든 하위묶음(코드에 없는 메뉴). key 는 `folder:<n>`. */
export type SidebarFolder = {
  key: string;
  label: string;
  /** 이 묶음이 속한 LNB 그룹 라벨 */
  group: string;
};

export type SidebarOrderPreference = {
  groups: string[];
  items: Record<string, string[]>;
  subItems: Record<string, string[]>;
  /** 매장이 화면에서 감춘 그룹·메뉴 키 목록. 권한이 아니라 화면 정리다(주소 직접 입력은 열린다). */
  hidden: string[];
  folders: SidebarFolder[];
};

/** 대표가 정한 매장 공용 왼쪽 메뉴 순서. 미설정이면 코드 기본 순서(null). */
export async function fetchSidebarOrder(): Promise<SidebarOrderPreference | null> {
  const { order } = await fetchJson<{ order: SidebarOrderPreference | null }>(
    `${API_V2}/tenants/me/sidebar-order`,
  );
  return order ?? null;
}

/** 대표 전용 저장. null은 저장된 힌트를 지워 코드 기본 순서로 되돌린다. */
export async function updateSidebarOrder(
  order: SidebarOrderPreference | null,
): Promise<SidebarOrderPreference | null> {
  const result = await fetchJson<{ order: SidebarOrderPreference | null }>(
    `${API_V2}/tenants/me/sidebar-order`,
    { method: "PUT", body: JSON.stringify({ order }) },
  );
  return result.order ?? null;
}

/** 신형 커스텀 slot 의 슬러그 key → 사람이 보는 항목명. 레거시 한글-key slot 은 여기 없음. */
export type FormDefaultLabels = Record<string, string>;

/** key → 해당 slot 을 참조하는 템플릿 개수 (내 tenant + SYSTEM 공용 기준). */
export type FormDefaultsUsage = Record<string, number>;

/** 항목을 새로 배치할 때 쓰는, 매장 공통의 항목별 마지막 박스 크기(자연 좌표 px). */
export type DefaultBoxSizes = Record<string, { w: number; h: number }>;

export interface FormDefaultsResponse {
  formDefaults: FormDefaults;
  labels: FormDefaultLabels;
  usage: FormDefaultsUsage;
  /** 회색 글자 진하기 %(1~100). null = 미설정 → DEFAULT_MUTED_OPACITY 폴백. */
  mutedOpacity: number | null;
  /** 매장 전체 기본 글꼴(CSS family). null = 미설정 → 시스템 기본(고딕). 박스별 글꼴이 우선. */
  defaultFontFamily: string | null;
  /** 매장 전체 글자 크기 배율 %(50~200). null = 미설정 → DEFAULT_FONT_SCALE(100) 폴백. */
  fontScale: number | null;
  /** 편집기에서 새 항목을 놓을 때의 기본 글자 크기(px, 7~200). null = 미설정 → 박스 높이 자동. */
  defaultFontSize: number | null;
  /** 텍스트·포스트잇을 새로 만들 때 쓸 별도 글꼴. 일반 항목 기본 글꼴과 독립이다. */
  noteDefaultFontFamily: string | null;
  /** 텍스트·포스트잇을 새로 만들 때 쓸 별도 글자 크기(px). */
  noteDefaultFontSize: number | null;
  /** 텍스트·포스트잇 편집 장식의 기본 불투명도(0~100). */
  noteControlsOpacity: number | null;
  /** 이름 자필·안내 확인 자필·서명/싸인을 새로 그릴 때의 펜 굵기(px, 4~20, 기본 12).
   *  고객 공개 입력폼과 매장이 직접 그리는 서명 패드가 같이 따른다 — 서명 칸 높이가 다르면
   *  scaleSignaturePenWidth 로 환산해 출력된 신청서에서 비슷한 굵기가 되게 한다. */
  signaturePenWidth: number;
  defaultBoxSizes: DefaultBoxSizes;
}

/** 기존 고정 회색 #d1d5db 와 같은 진하기 — 흰 바탕 위 검정 18% ≈ rgb(209,213,219). */
export const DEFAULT_MUTED_OPACITY = 18;

/** 글자 크기 배율 기본값 % — 100 = 박스마다 구워진 크기 그대로(기존 동작). */
export const DEFAULT_FONT_SCALE = 100;

/**
 * 새 항목 배치용 기본 글자 크기(px) — 매장이 따로 지정하지 않았을 때 쓰는 값.
 * 이 크기로 새 항목을 놓고, "전체 글자 설정" 패널의 슬라이더로 매장이 바꿀 수 있다.
 * (예전엔 미설정 시 박스 높이에서 자동 계산했지만, 이제 고정 기본값 50 으로 시작한다.)
 */
export const DEFAULT_PLACED_FONT_SIZE = 50;

/**
 * 매장 글자 크기 배율(%)을 곱셈 계수로 환산한다. 박스마다 구워진 폰트 크기에 이 값을
 * 곱해 그린다 — 화면 미리보기·인쇄(render.py 의 fs*font_scale)가 같은 계수를 쓴다.
 * 미설정·범위 밖이면 1.0(=100%)으로 폴백해 기존 크기를 유지한다.
 */
export function fontScaleFactor(scale: number | null | undefined): number {
  const s =
    typeof scale === "number" && scale >= 50 && scale <= 200
      ? scale
      : DEFAULT_FONT_SCALE;
  return s / 100;
}

/**
 * "회색으로" 토글된 글자의 CSS 색. 흰 바탕 위 검정 op% 라 인쇄 렌더(render.py 의
 * G=255-round(255*op/100) 환산)와 같은 회색이 나온다 — 화면·인쇄 색 일치의 핵심.
 */
export function mutedCssColor(opacity: number | null | undefined): string {
  const op =
    typeof opacity === "number" && opacity >= 1 && opacity <= 100
      ? opacity
      : DEFAULT_MUTED_OPACITY;
  return `rgba(0,0,0,${op / 100})`;
}

// 매장 기본값(formDefaults) 조회의 단일 표준 쿼리 키 — 서식 채우기·서식 상세·기본값
// 모달이 모두 이걸 쓴다. 과거 `["tenant","form-defaults"]`(단수) vs `["tenants","me",
// "form-defaults"]`(복수)로 키가 갈려 한쪽이 영원히 갱신 안 되는 버그가 있었다. 키를
// 여기 한 곳에 못박아 다시는 드리프트가 나지 않게 한다. 변경 시 무효화도 이 키로.
export const FORM_DEFAULTS_QUERY_KEY = ["tenants", "me", "form-defaults"] as const;

export async function fetchFormDefaults(): Promise<FormDefaultsResponse> {
  const {
    formDefaults,
    labels,
    usage,
    mutedOpacity,
    defaultFontFamily,
    fontScale,
    defaultFontSize,
    noteDefaultFontFamily,
    noteDefaultFontSize,
    noteControlsOpacity,
    signaturePenWidth,
    defaultBoxSizes,
  } = await fetchJson<FormDefaultsResponse>(`${API_V2}/tenants/me/form-defaults`);
  return {
    formDefaults: formDefaults ?? {},
    labels: labels ?? {},
    usage: usage ?? {},
    mutedOpacity: mutedOpacity ?? null,
    defaultFontFamily: defaultFontFamily ?? null,
    fontScale: fontScale ?? null,
    defaultFontSize: defaultFontSize ?? null,
    noteDefaultFontFamily: noteDefaultFontFamily ?? null,
    noteDefaultFontSize: noteDefaultFontSize ?? null,
    noteControlsOpacity: noteControlsOpacity ?? null,
    signaturePenWidth: normalizeSignaturePenWidth(signaturePenWidth),
    defaultBoxSizes: defaultBoxSizes ?? {},
  };
}

/** 텍스트·포스트잇에서 마지막으로 고른 편집값을 매장 공통 설정에 부분 저장한다. */
export async function updateNoteDefaults(values: {
  noteDefaultFontFamily?: string | null;
  noteDefaultFontSize?: number;
  noteControlsOpacity?: number;
}): Promise<void> {
  await fetchJson<{ ok: boolean }>(`${API_V2}/tenants/me/form-render-config`, {
    method: "PUT",
    body: JSON.stringify(values),
  });
}

/** 회색 글자 진하기 저장 (매장 공통 하나). null = 설정 해제(기본 회색 복귀). */
export async function updateMutedOpacity(
  mutedOpacity: number | null,
): Promise<void> {
  await fetchJson<{ ok: boolean }>(`${API_V2}/tenants/me/form-render-config`, {
    method: "PUT",
    body: JSON.stringify({ mutedOpacity }),
  });
}

/** 매장 전체 기본 글꼴 저장 (매장 공통 하나). null = 해제(시스템 기본 글꼴로 복귀). */
export async function updateDefaultFontFamily(
  defaultFontFamily: string | null,
): Promise<void> {
  await fetchJson<{ ok: boolean }>(`${API_V2}/tenants/me/form-render-config`, {
    method: "PUT",
    body: JSON.stringify({ defaultFontFamily }),
  });
}

/** 새 항목 배치용 기본 글자 크기 저장 (매장 공통 하나). null = 해제(박스 높이 자동으로 복귀). */
export async function updateDefaultFontSize(
  defaultFontSize: number | null,
): Promise<void> {
  await fetchJson<{ ok: boolean }>(`${API_V2}/tenants/me/form-render-config`, {
    method: "PUT",
    body: JSON.stringify({ defaultFontSize }),
  });
}

/** 항목 하나의 마지막 리사이즈 크기를 매장 공통 설정에 병합 저장한다. */
export async function updateDefaultBoxSize(
  fieldKey: string,
  size: { w: number; h: number },
): Promise<void> {
  await fetchJson<{ ok: boolean }>(`${API_V2}/tenants/me/form-render-config`, {
    method: "PUT",
    body: JSON.stringify({ defaultBoxSize: { fieldKey, ...size } }),
  });
}

/** 매장 전체 글자 크기 배율 저장 (매장 공통 하나). null = 해제(100%로 복귀). */
export async function updateFontScale(fontScale: number | null): Promise<void> {
  await fetchJson<{ ok: boolean }>(`${API_V2}/tenants/me/form-render-config`, {
    method: "PUT",
    body: JSON.stringify({ fontScale }),
  });
}

/** 고객이 새 자필서명을 그릴 때 쓸 매장 공통 펜 굵기 저장. 대표만 변경할 수 있다. */
export async function updateSignaturePenWidth(
  signaturePenWidth: number,
): Promise<void> {
  await fetchJson<{ ok: boolean }>(`${API_V2}/tenants/me/form-render-config`, {
    method: "PUT",
    body: JSON.stringify({ signaturePenWidth }),
  });
}

/**
 * 커스텀 slot 값 전량 저장. labels 를 넘기면 라벨 칼럼도 갱신하고,
 * 생략하면 서버가 기존 라벨을 보존한다(값만 바꾸는 호출이 라벨을 날리지 않도록).
 */
export async function updateFormDefaults(
  formDefaults: FormDefaults,
  labels?: FormDefaultLabels,
): Promise<FormDefaults> {
  const { formDefaults: saved } = await fetchJson<{ formDefaults: FormDefaults }>(
    `${API_V2}/tenants/me/form-defaults`,
    {
      method: "PUT",
      body: JSON.stringify(labels ? { formDefaults, labels } : { formDefaults }),
    },
  );
  return saved ?? {};
}

// ─── 요금제 변경 가능일 계산식 (매장당 하나) ──────────────────
//
// 매장이 상대식("D+185"·"M+3") 하나만 관리하고, 실제 날짜는 신청서 개통일 기준으로
// 그때그때 계산한다(부가서비스 유지기간과 같은 셈법). 날짜 계산 위젯이 읽고 저장한다.

/** 현재 매장의 요금제 변경 가능일 계산식. 미설정이면 null. */
export async function fetchPlanChangeExpr(): Promise<string | null> {
  const { expr } = await fetchJson<{ expr: string | null }>(
    `${API_V2}/tenants/me/plan-change-expr`,
  );
  return expr ?? null;
}

/** 계산식 저장. 빈 문자열은 서버가 미설정(null)로 정규화한다. 저장된 값을 돌려준다. */
export async function updatePlanChangeExpr(
  expr: string,
): Promise<string | null> {
  const { expr: saved } = await fetchJson<{ expr: string | null }>(
    `${API_V2}/tenants/me/plan-change-expr`,
    {
      method: "PUT",
      body: JSON.stringify({ expr }),
    },
  );
  return saved ?? null;
}

// ─── Tenants (목록 / 전환) ─────────────────────────────

export interface TenantItem {
  id: string;
  name: string;
  status: string;
  createdAt: string;
}

export interface TenantsResponse {
  currentTenantId: string | null;
  tenants: TenantItem[];
}

export function fetchTenants(): Promise<TenantsResponse> {
  return fetchJson(`${API_V2}/tenants`);
}

/**
 * 보는 매장 전환.
 * - 본사 관리자: 그 매장으로 "임시 보기" 진입(본진 신분 유지).
 * - 매장대표/직원: 본진 매장 전환.
 *
 * 미선택(본사) 복귀는 폐지 — 서버가 tenantId=null 을 400 으로 거부한다. 관리자는 항상
 * 어떤 매장을 보는 상태로 둔다.
 */
export function switchTenant(
  tenantId: string,
): Promise<{ tenantId: string | null; tenantName: string | null }> {
  return fetchJson(`${API_V2}/tenants/switch`, {
    method: "PUT",
    body: JSON.stringify({ tenantId }),
  });
}
